/**
 * Copyright 2026 Perform Content Services Limited. Confidential and Proprietary. All Rights Reserved.
 */
import {Dispatch} from 'redux';

import websocket from 'services/websocket.connections';
import {fetchAllTimeToFirstFrame} from 'services/analytics.service';
import {ITimeToFirstFrameCluster} from 'interfaces';
import {IPortalError, transformToPortalError} from 'utility/error-handler';

/**
* ACTION TYPES
*/

export const TIME_TO_FIRST_FRAME_FETCHING = 'time-to-first-frame::TIME_TO_FIRST_FRAME_FETCHING' as const;
export const TIME_TO_FIRST_FRAME_FETCHING_DONE = 'time-to-first-frame::TIME_TO_FIRST_FRAME_FETCHING_DONE' as const;
export const TIME_TO_FIRST_FRAME_FETCHING_ERROR = 'time-to-first-frame::TIME_TO_FIRST_FRAME_FETCHING_ERROR' as const;

/**
* INTERFACES
*/

interface IRequestTimeToFirstFrame {
  type: typeof TIME_TO_FIRST_FRAME_FETCHING;
}
interface IReceiveTimeToFirstFrame {
  type: typeof TIME_TO_FIRST_FRAME_FETCHING_DONE;
  payload: {data: ITimeToFirstFrameCluster};
}
interface IFailedTimeToFirstFrame {
  type: typeof TIME_TO_FIRST_FRAME_FETCHING_ERROR;
  error: null | IPortalError;
}

export type ITimeToFirstFrameActionType = IRequestTimeToFirstFrame | IReceiveTimeToFirstFrame | IFailedTimeToFirstFrame;

export interface ITimeToFirstFrameActions {
  request: () => ITimeToFirstFrameActionType;
  receive: (payload) => ITimeToFirstFrameActionType;
  failed: (error: null | IPortalError) => ITimeToFirstFrameActionType;
}

/**
* ACTIONS
*/

const timeToFirstFrameActions: ITimeToFirstFrameActions = {
  request: () => ({type: TIME_TO_FIRST_FRAME_FETCHING}),
  receive: payload => ({
    type: TIME_TO_FIRST_FRAME_FETCHING_DONE,
    payload
  }),
  failed: error => ({
    type: TIME_TO_FIRST_FRAME_FETCHING_ERROR,
    error
  })
};

export const getTimeToFirstFrame = (start: number, end: number, applicationId: string) => async(dispatch: Dispatch<ITimeToFirstFrameActionType>): Promise<ITimeToFirstFrameCluster> => {
  const {
    request,
    receive,
    failed
  } = timeToFirstFrameActions;

  dispatch(request());

  try {
    const socket = websocket.getActiveSocket();
    const timeToFirstFrame = await fetchAllTimeToFirstFrame(socket, start, end, applicationId);

    dispatch(receive({data: timeToFirstFrame}));

    return timeToFirstFrame;
  } catch (e) {
    const portalError = transformToPortalError(e);

    dispatch(failed(portalError));

    throw portalError;
  }
};